import { useEffect, useState } from "react";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { fetchENSOStatus, ENSOStatus } from "../utils/fetchENSOStatus";
import { LineChart, Line, ResponsiveContainer } from "recharts";

const getTheme = (phase: string) => {
  if (phase === "El Niño") {
    return {
      bg: "from-red-600/20 via-orange-500/10 to-amber-400/10",
      border: "border-[.25rem] border-red-500/40",
      text: "text-red-300",
      accent: "bg-red-500",
    };
  }

  if (phase === "La Niña") {
    return {
      bg: "from-blue-600/20 via-cyan-500/10 to-sky-400/10",
      border: "border-[.25rem] border-blue-500/40",
      text: "text-blue-300",
      accent: "bg-blue-500",
    };
  }

  return {
    bg: "from-zinc-600/20 to-zinc-500/10",
    border: "border-[.25rem] border-zinc-500/30",
    text: "text-zinc-300",
    accent: "bg-zinc-500",
  };
};

const getTrendIcon = (trend: string) => {
  if (trend === "strengthening") return <ArrowUpRight size={16} />;
  if (trend === "weakening") return <ArrowDownRight size={16} />;
  return <Minus size={16} />;
};

interface ENSOCardProps {
  latitude?: number | null;
  longitude?: number | null;
  onMoreInfo?: (data: ENSOStatus | null) => void;
}

export default function ENSOCard({
  latitude,
  longitude,
  onMoreInfo,
}: ENSOCardProps) {
  const [enso, setEnso] = useState<ENSOStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    console.log("[ENSOCard] Fetching ENSO status from NOAA...");
    fetchENSOStatus({ latitude, longitude })
      .then((data) => {
        console.log("[ENSOCard] ENSO data fetched:", data);
        setEnso(data);
        setError(null);
      })
      .catch((e) => {
        console.error("[ENSOCard] Error fetching ENSO status:", e);
        setError("NOAA data unavailable");
        setEnso(null);
      })
      .finally(() => {
        setLoading(false);
        console.log("[ENSOCard] ENSO fetch complete.");
      });
  }, [latitude, longitude]);

  const theme = getTheme(enso?.phase || "Neutral");

  const hasValidEnso = Boolean(
    enso && Array.isArray(enso.nino34) && enso.nino34.length > 0,
  );
  const statusLabel = loading
    ? "Loading"
    : error || !hasValidEnso
      ? "Offline"
      : "Live";
  const statusClasses = loading
    ? "bg-amber-500/15 text-amber-400"
    : error || !hasValidEnso
      ? "bg-rose-500/15 text-rose-400"
      : "bg-emerald-500/15 text-emerald-400";
  const statusDotClasses = loading
    ? "bg-amber-400 animate-pulse"
    : error || !hasValidEnso
      ? "bg-rose-400"
      : "bg-emerald-400 animate-pulse";

  return (
    <Card
      className={`w-full rounded-lg ${theme.border} bg-gradient-to-br ${theme.bg} border-[.25rem] h-full min-h-[10rem] xl:aspect-square ${onMoreInfo ? "cursor-pointer hover:shadow-lg hover:ring-2 hover:ring-primary/30 transition-shadow duration-200" : ""}`}
      onClick={
        onMoreInfo ? () => onMoreInfo(hasValidEnso ? enso : null) : undefined
      }
    >
      <CardContent className="w-full p-4 xl:p-6 flex flex-col items-center justify-between text-center relative">
        {/* Header */}
        <div className="w-full flex items-start justify-between">
          <p className="text-xs xl:text-sm text-muted-foreground font-semibold leading-tight">
            ENSO Status
          </p>
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[0.6rem] font-semibold uppercase tracking-wide ${statusClasses}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${statusDotClasses}`} />
            {statusLabel}
          </span>
        </div>

        {/* Main Content */}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-2 flex flex-col items-center justify-center">
            <span
              className={`text-2xl lg:text-3xl xl:text-4xl font-bold leading-none whitespace-nowrap ${theme.text}`}
            >
              {loading ? "—" : error ? "?" : enso?.phase}
            </span>
          </div>
          {/* Sparkline for Niño 3.4 index 
          <div className="w-20 h-10 flex items-center justify-center">
            {enso?.nino34 && enso.nino34.length > 0 && (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={enso.nino34.map((v, i) => ({ i, v }))}>
                  <Line
                    type="monotone"
                    dataKey="v"
                    stroke={enso?.phase === "El Niño" ? "#f87171" : "#60a5fa"}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          */}
        </div>
        {/* Status */}
        <p className="text-sm text-muted-foreground font-semibold mt-1">
          --
          {loading
            ? " Loading..."
            : error || !hasValidEnso
              ? " Offline"
              : ` ${enso?.strength}`}
          --
        </p>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between text-sm">
          {/* Trend indicator */}
          <div className="flex items-center gap-1 text-muted-foreground">
            {getTrendIcon(hasValidEnso ? enso?.trend || "stable" : "stable")}
            <span className="capitalize">
              {loading
                ? "—"
                : error || !hasValidEnso
                  ? "—"
                  : enso?.trend || "—"}
            </span>
          </div>
        </div>

        {/* Impact */}
        <div className="my-3 text-xs text-muted-foreground">
          {loading
            ? ""
            : !hasValidEnso
              ? "No ENSO data available"
              : enso?.impact}
        </div>

        {/* Data source label */}
        <div className="absolute bottom-2 text-center text-[0.6rem] text-gray-400 mt-4">
          {/* Last updated */}
          <p className="text-muted-foreground">
            {loading ? "" : enso?.updated}
          </p>
          <p>Source: NOAA</p>
        </div>
      </CardContent>
    </Card>
  );
}
